import { useState, useEffect } from 'react';
import { odooClient, type Activity } from '../services/odooClient';
import { useAuthStore } from '../store/authStore';

export const useGlobalActivities = () => {
  const { user } = useAuthStore();
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState<number>(0);

  // Fetch activities for all contacts
  const fetchAllActivities = async () => {
    setLoading(true);
    try {
      // Use the new public method from odooClient
      const activities = await odooClient.getAllActivities();
      
      // Also get completed activities from localStorage (if needed in future)
      const completedActivities: Activity[] = [];
      
      setAllActivities([...activities, ...completedActivities]);
      setLastFetch(Date.now());
    } catch (error) {
      console.error('❌ Failed to fetch global activities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get today/future activities (not overdue) for current user only
  const getTodayAndFutureActivities = (): Activity[] => {
    if (!user) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return allActivities.filter(activity => {
      // Only show activities for current user (created by or assigned to)
      // Convert user.id to number for comparison since activity IDs are numbers
      const userId = Number(user.id);
      const isUserActivity = activity.createdBy.id === userId || activity.assignedTo.id === userId;
      if (!isUserActivity) return false;
      
      // Skip completed/cancelled activities
      if (activity.state === 'done' || activity.state === 'cancel') return false;
      
      // Only today or future activities
      const activityDate = new Date(activity.dueDate);
      activityDate.setHours(0, 0, 0, 0);
      
      return activityDate >= today;
    });
  };

  // Get today-only activities (due today specifically)
  const getTodayOnlyActivities = (): Activity[] => {
    if (!user) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return allActivities.filter(activity => {
      // Only show activities for current user (created by or assigned to)
      const userId = Number(user.id);
      const isUserActivity = activity.createdBy.id === userId || activity.assignedTo.id === userId;
      if (!isUserActivity) return false;
      
      // Skip completed/cancelled activities
      if (activity.state === 'done' || activity.state === 'cancel') return false;
      
      // Only activities due TODAY (not future)
      const activityDate = new Date(activity.dueDate);
      activityDate.setHours(0, 0, 0, 0);
      
      return activityDate.getTime() === today.getTime();
    });
  };

  // Get count of today-only activities
  const getTodayOnlyCount = (): number => {
    return getTodayOnlyActivities().length;
  };

  // Get count of today/future activities
  const getTodayAndFutureCount = (): number => {
    return getTodayAndFutureActivities().length;
  };

  // Auto-fetch on mount and refresh every 2 minutes for faster updates
  useEffect(() => {
    fetchAllActivities();
    
    const interval = setInterval(() => {
      fetchAllActivities();
    }, 2 * 60 * 1000); // 2 minutes for quicker updates
    
    return () => clearInterval(interval);
  }, []);

  // Also refetch when user changes (in case of user switching)
  useEffect(() => {
    if (user) {
      fetchAllActivities();
    }
  }, [user?.id]);

  // Register refresh callback with odooClient for global activity updates
  useEffect(() => {
    odooClient.setGlobalActivityRefreshCallback(fetchAllActivities);
    
    return () => {
      odooClient.setGlobalActivityRefreshCallback(() => {});
    };
  }, []);

  return {
    allActivities,
    loading,
    getTodayAndFutureActivities,
    getTodayAndFutureCount,
    getTodayOnlyActivities,
    getTodayOnlyCount,
    refreshActivities: fetchAllActivities,
    lastFetch
  };
}; 